// Minimum progress required before surfacing an ETA. Below this the
// elapsed/progress ratio is too noisy to produce a stable estimate.
const MIN_PROGRESS_FOR_ETA = 2;

// Minimum elapsed time before surfacing an ETA, for the same reason — a tiny
// denominator gives wild estimates.
const MIN_ELAPSED_MS_FOR_ETA = 1500;

// Cap ETA display to stop runaway estimates on near-stalled uploads.
const MAX_ETA_SECONDS = 24 * 60 * 60; // 24h

// Baseline should be the first non-zero XHR progress event, not row creation.
// Compression and presign work can take several seconds while progress stays
// at 0%; counting that toward elapsed time systematically inflates the ETA.
export const computeEtaSeconds = (
  progress: number,
  baselineAt: number,
  now: number
): number | null => {
  if (progress >= 100) return null;
  if (progress < MIN_PROGRESS_FOR_ETA) return null;

  const elapsedMs = now - baselineAt;
  if (elapsedMs < MIN_ELAPSED_MS_FOR_ETA) return null;
  if (elapsedMs <= 0) return null;

  const remainingMs = (elapsedMs * (100 - progress)) / progress;
  const remainingSeconds = Math.round(remainingMs / 1000);

  if (remainingSeconds <= 0) return null;
  if (remainingSeconds > MAX_ETA_SECONDS) return null;

  return remainingSeconds;
};

export const formatEta = (seconds: number): string => {
  if (seconds < 60) {
    return `~${Math.max(1, seconds)}s left`;
  }
  if (seconds < 60 * 60) {
    const minutes = Math.round(seconds / 60);
    return `~${minutes}m left`;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (minutes === 0) return `~${hours}h left`;
  return `~${hours}h ${minutes}m left`;
};
